/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { DESKTOP_MIN_WIDTH } from '@/components/blocks/PLPProductCard/constants';
import { Sidebar } from '@/components/blocks/SideBar';
import { FacetNavigationItemChange } from '@/components/content/FacetNavigation/parts/ItemChange';
import { FacetNavigationPriceChange } from '@/components/content/FacetNavigation/parts/PriceChange';
import { SLPLPFacets } from '@/components/skeleton/components/elements/PLP';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { isEmpty } from 'lodash';
import { FC, useMemo } from 'react';

export const FacetNavigation: FC<{ id: ID }> = ({ id }) => {
	const facetNavigation = useFacetNavigation(id);
	const { sortOptions, selectedSortOption, onSortOptionChange } = useCatalogEntryList(id);
	const { Labels, Actions } = useLocalization('ProductFilter');
	const Facet = useLocalization('Facet');
	const { available, loading, filteredParams, facets, facetFilters } = facetNavigation;
	const { searchTerm } = filteredParams;
	const showFilter = useMemo(
		() => facets.length || !isEmpty(facetFilters) || (available && !searchTerm),
		[facets, facetFilters, available, searchTerm]
	);

	if (loading) return <SLPLPFacets />;

	if (!available && !searchTerm) return null;

	return (
		<ContentProvider
			value={{ ...facetNavigation, sortOptions, selectedSortOption, onSortOptionChange }}
		>
			<Sidebar
				sidebarPaperSx={{
					borderRadius: 0,
					maxWidth: 'auto',
					[`@media (min-width: ${DESKTOP_MIN_WIDTH - 1}px)`]: { maxWidth: '250px' },
				}}
				mobileBreakpoint={DESKTOP_MIN_WIDTH}
				scrollable
				elevation={0}
				messages={{
					action: showFilter ? Actions.filter.t() : Labels.noFilters.t(),
					drawer: Facet.Labels.title.t(),
				}}
			>
				<FacetNavigationPriceChange />
				<FacetNavigationItemChange />
			</Sidebar>
		</ContentProvider>
	);
};
